import moment from "moment";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import { JollyRancherChip } from "veneer/Chip";
import {
  AppliedFilter,
  AvailableFilterOption,
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { Application } from "cortex/ui/src/fleet/view/application/model";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import ButtonGroup from "cortex/ui/src/ui/button-group";
import GroupByTable, {
  type GroupByOption,
  type GroupedTableRow,
} from "cortex/ui/src/ui/table/component/GroupedTable";
import {
  formatApplicationSource,
  formatDateTimeLocal,
  formatDeviceName,
} from "cortex/ui/src/ui/table/formats";
import { downloadCSVFile } from "cortex/ui/src/util/csv";

import EventMessageCell from "./EventMessageCell";
import EventTableFilters, {
  EVENT_DATE_PRESETS,
  EventFilterId,
  EventFilterOptions,
  EventTableFilterState,
  createDefaultEventDateRange,
  createDefaultEventTableFilters,
  normalizeEventTableFilters,
} from "./EventTableFilters";
import { Event, EventRow, PRIORITIES } from "../model";

interface EventTableProps {
  applications: Record<string, Application>;
  device?: Device;
  devices: Record<string, Device>;
  emptyState?: ReactNode;
  events: Event[];
  filterOptions?: EventFilterOptions;
  filters?: EventTableFilterState;
  onFilterInteractionChange?: (isInteracting: boolean) => void;
  onFiltersChange?: Dispatch<SetStateAction<EventTableFilterState>>;
  tableSettingsKey?: string;
}

const column = createColumnHelper<EventRow>();
const filter = createFilter<EventRow>(
  "deviceName",
  "sourceName",
  "message",
  "name",
  "source",
  "severityLabel",
);

const AVAILABLE_FILTERS: ReadonlyArray<AvailableFilterOption> = [
  { disabled: true, id: "eventDate", label: "Time Range" },
  { id: "device", label: "Device" },
  { id: "name", label: "Name" },
  { id: "priority", label: "Priority" },
  { id: "source", label: "Source" },
  { id: "tags", label: "Tags" },
];

const EMPTY_FILTER_OPTIONS: EventFilterOptions = {
  deviceOptions: [],
  eventNameOptions: [],
  priorityOptions: [],
  sourceOptions: [],
  tagOptions: [],
};

const getCompleteEventDate = (
  currentFilters: EventTableFilterState,
): Pick<EventTableFilterState, "eventDate" | "eventDatePreset"> => {
  if (currentFilters.eventDate[0] && currentFilters.eventDate[1]) {
    return {
      eventDate: currentFilters.eventDate,
      eventDatePreset: currentFilters.eventDatePreset,
    };
  }

  return {
    eventDate: createDefaultEventDateRange(),
    eventDatePreset: "lastHour",
  };
};

const isTargetVisible = (
  target: string,
  visibleFilters: ReadonlyArray<EventFilterId>,
): boolean => {
  if (target.startsWith("d_")) {
    return visibleFilters.includes("device");
  }

  if (target.startsWith("t_")) {
    return visibleFilters.includes("tags");
  }

  return false;
};

function formatExportTimestamp(): string {
  return moment().format("YYYY-MM-DD[T]HH_mm_ssZ").replace(":", "_");
}

function formatEventRow(
  applications: Record<string, Application>,
  devices: Record<string, Device>,
  event: Event,
): EventRow {
  return {
    ...event,
    deviceId: devices[event.did] ? devices[event.did].device_id : null,
    deviceName: formatDeviceName(event.did, devices),
    severityLabel: PRIORITIES[event.severity]
      ? PRIORITIES[event.severity].label
      : `Unknown (${event.severity})`,
    sourceName: formatApplicationSource(applications, event.source),
  };
}

export default function EventTable({
  applications,
  device,
  devices,
  emptyState,
  events,
  filterOptions = EMPTY_FILTER_OPTIONS,
  filters: controlledFilters,
  onFilterInteractionChange,
  onFiltersChange: controlledOnFiltersChange,
  tableSettingsKey = "events_acct_0",
}: EventTableProps) {
  const [internalFilters, setInternalFilters] = useState(
    createDefaultEventTableFilters,
  );
  const filters = controlledFilters ?? internalFilters;
  const onFiltersChange = controlledOnFiltersChange ?? setInternalFilters;
  const [groupedRows, setGroupedRows] = useState<
    ReadonlyArray<GroupedTableRow<EventRow>>
  >([]);

  const formattedEvents =
    useMemo(
      () => events.map((event) => formatEventRow(applications, devices, event)),
      [applications, devices, events],
    ) || [];

  const availableFilters = useMemo(
    () =>
      AVAILABLE_FILTERS.filter(
        ({ id }) => !device || (id !== "device" && id !== "tags"),
      ),
    [device],
  );
  const visibleFilters = useMemo(() => {
    const validVisibleFilters = filters.visibleFilters.filter((id) =>
      availableFilters.some((option) => option.id === id),
    );

    return validVisibleFilters.includes("eventDate")
      ? validVisibleFilters
      : [...validVisibleFilters, "eventDate" as const];
  }, [availableFilters, filters.visibleFilters]);
  const visibleFilterState = useMemo(
    () => ({
      ...filters,
      targets: device ? [] : filters.targets,
      visibleFilters,
    }),
    [device, filters, visibleFilters],
  );

  const handleVisibleFiltersChange = useCallback(
    (selectedFilterIds: ReadonlyArray<string>) => {
      const nextVisibleFilters = selectedFilterIds.filter(
        (id): id is EventFilterId =>
          availableFilters.some((option) => option.id === id),
      );
      if (!nextVisibleFilters.includes("eventDate")) {
        nextVisibleFilters.push("eventDate");
      }

      onFiltersChange((previous) => ({
        ...previous,
        ...getCompleteEventDate(previous),
        name: nextVisibleFilters.includes("name") ? previous.name : [],
        priority: nextVisibleFilters.includes("priority")
          ? previous.priority
          : [],
        source: nextVisibleFilters.includes("source") ? previous.source : [],
        targets: previous.targets.filter((target) =>
          isTargetVisible(target, nextVisibleFilters),
        ),
        visibleFilters: nextVisibleFilters,
      }));
    },
    [availableFilters, onFiltersChange],
  );

  const clearFilters = useCallback(() => {
    onFiltersChange((previous) => ({
      eventDate: createDefaultEventDateRange(),
      eventDatePreset: "lastHour",
      name: [],
      priority: [],
      source: [],
      targets: [],
      visibleFilters: previous.visibleFilters,
    }));
  }, [onFiltersChange]);

  const handleApplySavedFilters = useCallback(
    (savedFilters: unknown) => {
      const normalized = normalizeEventTableFilters(savedFilters);
      onFiltersChange(
        device
          ? {
              ...normalized,
              targets: [],
              visibleFilters: normalized.visibleFilters.filter(
                (id) => id !== "device" && id !== "tags",
              ),
            }
          : normalized,
      );
    },
    [device, onFiltersChange],
  );

  const getAppliedFilters = useCallback(
    (
      id: "name" | "priority" | "source",
      label: string,
      options: ReadonlyArray<{ label: string; value: unknown }>,
    ): ReadonlyArray<AppliedFilter> =>
      filters[id].map((selectedValue) => ({
        key: `${id}-${selectedValue}`,
        label,
        onDelete: () =>
          onFiltersChange((previous) => ({
            ...previous,
            [id]: previous[id].filter((value) => value !== selectedValue),
          })),
        value:
          options.find(({ value }) => String(value) === selectedValue)?.label ??
          selectedValue,
      })),
    [filters, onFiltersChange],
  );

  const getTargetAppliedFilters = useCallback(
    (
      id: "device" | "tags",
      label: string,
      options: ReadonlyArray<{ label: string; value: unknown }>,
      targetPrefix: "d_" | "t_",
    ): ReadonlyArray<AppliedFilter> =>
      filters.targets
        .filter((target) => target.startsWith(targetPrefix))
        .map((selectedValue) => ({
          key: `${id}-${selectedValue}`,
          label,
          onDelete: () =>
            onFiltersChange((previous) => ({
              ...previous,
              targets: previous.targets.filter(
                (value) => value !== selectedValue,
              ),
            })),
          value:
            options.find(({ value }) => String(value) === selectedValue)
              ?.label ?? selectedValue,
        })),
    [filters.targets, onFiltersChange],
  );

  const eventDateAppliedFilter = useMemo<ReadonlyArray<AppliedFilter>>(() => {
    const [startDate, endDate] = filters.eventDate;
    if (!visibleFilters.includes("eventDate") || !startDate || !endDate) {
      return [];
    }

    const dateFormat = "MMM Do, YYYY hh:mm A";
    const formattedStartDate = formatDateTimeLocal(startDate, dateFormat);
    const formattedEndDate = formatDateTimeLocal(endDate, dateFormat);
    const presetLabel = EVENT_DATE_PRESETS.find(
      ({ id }) => id === filters.eventDatePreset,
    )?.label;
    const absoluteRange = `${formattedStartDate} - ${formattedEndDate}`;

    return [
      {
        key: "eventDate",
        label: "Time Range",
        value: presetLabel
          ? `${presetLabel} (${absoluteRange})`
          : absoluteRange,
      },
    ];
  }, [filters.eventDate, filters.eventDatePreset, visibleFilters]);

  const appliedFilters = useMemo(
    () => [
      ...eventDateAppliedFilter,
      ...getTargetAppliedFilters(
        "device",
        "Device",
        filterOptions.deviceOptions,
        "d_",
      ),
      ...getAppliedFilters("name", "Name", filterOptions.eventNameOptions),
      ...getAppliedFilters(
        "priority",
        "Priority",
        filterOptions.priorityOptions,
      ),
      ...getAppliedFilters("source", "Source", filterOptions.sourceOptions),
      ...getTargetAppliedFilters(
        "tags",
        "Tags",
        filterOptions.tagOptions,
        "t_",
      ),
    ],
    [
      device,
      eventDateAppliedFilter,
      filterOptions,
      getAppliedFilters,
      getTargetAppliedFilters,
    ],
  );

  const tableFilterProps = {
    appliedFilters: {
      filters: appliedFilters,
      onClearAll: clearFilters,
    },
    availableFilters: {
      filters: availableFilters,
      onChange: handleVisibleFiltersChange,
      onInteractionChange: onFilterInteractionChange,
      selectedFilterIds: visibleFilters,
    },
    dataTestId: {
      appliedFilters: "events-applied-filters",
      availableFilters: "events-available-filters",
      savedFilters: "events-saved-filters",
    },
    savedFilters: {
      currentFilters: visibleFilterState,
      onApply: handleApplySavedFilters,
      saveDialogMessage:
        filters.eventDatePreset === null &&
        filters.eventDate[0] &&
        filters.eventDate[1] ? (
          <Alert dense severity="info">
            This custom date and time range will remain the same when the saved
            filter set is reapplied.
          </Alert>
        ) : undefined,
    },
    settingsKey: tableSettingsKey,
    viewOptions: visibleFilters.length > 0 && (
      <EventTableFilters
        {...filterOptions}
        onChange={onFiltersChange}
        onInteractionChange={onFilterInteractionChange}
        value={visibleFilterState}
      />
    ),
  };

  const deviceIdByDeviceName = useMemo(
    () =>
      formattedEvents.reduce<Record<string, string | null>>((acc, event) => {
        if (event.deviceName) {
          acc[event.deviceName] = event.deviceId;
        }
        return acc;
      }, {}),
    [formattedEvents],
  );

  const columns = useMemo(
    () => [
      column.accessor("deviceName", {
        header: "Device",
        cell: (prop) => {
          const row = prop.row.original;
          const value = prop.getValue();
          if (!row.deviceId) {
            return value;
          }
          return <Link to={`/devices/${row.deviceId}`}>{value}</Link>;
        },
        meta: {
          width: 200,
        },
        sortingFn: "alphanumeric",
      }),
      column.accessor("name", {
        header: "Name",
        cell: (prop) => prop.getValue(),
        sortingFn: "alphanumeric",
        meta: {
          width: 250,
        },
      }),
      column.accessor("severity", {
        header: "Priority",
        cell: (prop) => {
          const value = prop.getValue();
          const { color, label } =
            PRIORITIES[value] ||
            ({
              color: "gray",
              label: "Unknown",
            } as const);
          return <JollyRancherChip color={color} label={label} />;
        },
        sortingFn: sorting.number((row) => row.severity),
        meta: {
          width: 125,
        },
      }),
      column.accessor("message", {
        header: "Message",
        cell: (prop) => <EventMessageCell message={prop.getValue()} />,
        enableSorting: false,
      }),
      column.accessor("event_date", {
        header: "Date",
        cell: (prop) =>
          formatDateTimeLocal(prop.getValue(), "MMM Do, YYYY hh:mm:ss a"),
        sortingFn: sorting.datetime((row) => row.event_date),
        meta: {
          width: 200,
        },
      }),
      column.accessor("sourceName", {
        header: "Source",
        cell: (prop) => prop.getValue(),
        sortingFn: "alphanumeric",
        meta: {
          width: 200,
        },
      }),
    ],
    [applications, devices],
  );

  const downloadRowsAsCSV = (
    headers: string[],
    rows: Array<Array<string | number | null | undefined>>,
    filenamePrefix: string,
  ) => {
    const currentTime = formatExportTimestamp();

    downloadCSVFile({
      filename: `${filenamePrefix}-${currentTime}.csv`,
      headers,
      rows,
    });
  };

  const handleExportEvents = () => {
    if (!formattedEvents.length) {
      return;
    }

    const headers = [
      "Device",
      "DeviceId",
      "Name",
      "Priority",
      "Message",
      "Date",
      "Source",
    ];

    const rows = formattedEvents.map((event) => [
      event.deviceName,
      event.deviceId,
      event.name,
      event.severityLabel,
      event.message,
      formatDateTimeLocal(event.event_date, "MMM Do, YYYY hh:mm:ss a"),
      event.sourceName,
    ]);

    downloadRowsAsCSV(headers, rows, "cortex-events");
  };

  const handleExportGroups = () => {
    if (!groupedRows.length) {
      return;
    }

    const activeGroupBy = groupedRows[0].groupBy;

    const headers = activeGroupBy.flatMap((groupId: string) => {
      const option = groupByOptions.find((opt) => opt.id === groupId);
      const label = option?.label ?? groupId;

      return groupId === "deviceName" ? [label, "DeviceId"] : [label];
    });

    headers.push("Total");

    const sortedGroups = [...groupedRows].sort((a, b) => b.count - a.count);

    const rows: Array<Array<string | number | null | undefined>> =
      sortedGroups.map((group) => {
        const groupValues = activeGroupBy.reduce<
          Array<string | number | null | undefined>
        >((acc, groupId: string) => {
          const rawValue = group[groupId] as string;
          const option = groupByOptions.find((opt) => opt.id === groupId);

          let formattedValue: string;

          if (option?.dateFormat) {
            formattedValue = formatDateTimeLocal(rawValue, option.dateFormat);
          } else if (groupId === "severity") {
            formattedValue =
              PRIORITIES[Number(rawValue)]?.label ?? `Unknown (${rawValue})`;
          } else {
            formattedValue = String(rawValue ?? "");
          }

          if (groupId === "deviceName") {
            const deviceId = String(
              group.deviceId ??
                deviceIdByDeviceName[String(rawValue ?? "")] ??
                "",
            );
            acc.push(formattedValue, deviceId);
            return acc;
          }

          acc.push(formattedValue);
          return acc;
        }, []);

        return [...groupValues, group.count];
      });

    downloadRowsAsCSV(headers, rows, "cortex-event-groups");
  };

  const emptyStateComponent = (
    <Box p={2} textAlign="center">
      {typeof emptyState === "string" || typeof emptyState === "number" ? (
        <Typography color={Colors.gray1000} component="span" variant="body2">
          {emptyState}
        </Typography>
      ) : (
        emptyState
      )}
    </Box>
  );

  const groupByOptions = useMemo<ReadonlyArray<GroupByOption>>(
    () => [
      ...(device ? [] : [{ id: "deviceName", label: "Device" }]),
      { id: "severity", label: "Priority" },
      { id: "sourceName", label: "Source" },
      { id: "name", label: "Name" },
      {
        dateFormat: "MMM Do, YYYY",
        id: "event_date_day",
        label: "Date",
        sourceColumnId: "event_date",
      },
      {
        dateFormat: "HH:00 - HH:59",
        id: "event_date_hour",
        label: "Hour",
        sourceColumnId: "event_date",
      },
    ],
    [device],
  );

  const exportButton = (
    <ButtonGroup
      split={{
        primary: {
          label: "Export Events",
          onClick: () => handleExportEvents(),
        },
        items: [
          {
            label: "Events",
            onClick: () => handleExportEvents(),
          },
          {
            disabled: !groupedRows.length,
            label: "Groups",
            onClick: () => handleExportGroups(),
          },
        ],
      }}
    />
  );

  return (
    <GroupByTable
      columns={columns}
      emptyState={emptyStateComponent}
      filter={filter}
      groupByOptions={groupByOptions}
      hideColumns={device ? ["deviceName"] : []}
      initialPageSize={10}
      primaryActions={exportButton}
      rows={formattedEvents}
      setGroupedRows={setGroupedRows}
      {...tableFilterProps}
    />
  );
}
